import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// The dashboard/KPI domain is re-exported through the barrel; the Bexio and
// revenue-analytics domains are not, and the repo imports those from their own
// modules (see AdminRevenueAnalyticsPage). Following that split rather than
// widening the barrel keeps this slice out of a shared file it has no reason to
// touch — and a barrel import of a name that is not there resolves to undefined
// at runtime, not to a build error.
import {
  fetchDashboardCostBreakdown,
  fetchAdminOccupancy,
  fetchAdminOccupancyRoomDays,
  fetchAdminAlerts,
  fetchAdminCriticalUnits,
  sanitizeClientErrorMessage,
} from "../../../api/adminData";
import { fetchRevenueAnalytics } from "../../../api/adminData/revenueAnalytics";
import {
  fetchBexioCompanyInvoiced,
  fetchBexioRecentPayments,
  fetchBexioStatus,
} from "../../../api/adminData/bexio";
import {
  overviewWindowKey,
  roomDaysParams,
  selectOverviewWindow,
} from "./overviewSelectors";

/**
 * All reads for the company-overview tab, in one place.
 *
 * WHY THE TAB OWNS ITS DATA
 * -------------------------
 * `AdminUebersichtPage` mounts all four tab panels at once, so fetching here rather
 * than in the page keeps Finanzen, Performance and Rechnungen from firing nine
 * overview requests they never display. The hook is inert until `enabled` is true.
 *
 * THE RANGE IS NO LONGER COLLAPSED (P3b)
 * --------------------------------------
 * The page used to hand this hook `kpisPeriod.to` — a single month — so a filter
 * reading "April – September 2026" asked every endpoint for September alone. It now
 * receives the global `{mode, from, to}` and resolves it ONCE, in
 * `selectOverviewWindow`, into the window the P3a contracts take.
 *
 * ONE REQUEST PER CONTRACT, NEVER ONE PER MONTH. A six-month period is five
 * requests in total, exactly as a single month is. The folding lives in the
 * services — `range_aggregation: "sum_of_monthly_truths"` on the wire — and there is
 * no arithmetic in this file and must never be: summing months in the browser would
 * be a second KPI truth beside the one the backend publishes.
 *
 * TWO AXES, DELIBERATELY SEPARATE
 * -------------------------------
 * Period-aware reads (revenue analytics, company invoiced, cost breakdown, C.4
 * room-day occupancy, the payment feed) re-run when the period changes.
 * Current-state reads (Bexio status, warnings, the occupancy snapshot) run ONCE and
 * are never given a period.
 *
 * The two occupancy reads sit on OPPOSITE sides of that line on purpose. C.4
 * describes the selected period; the C.1 snapshot describes today. Moving to a
 * six-month window must not make "aktuell belegt" mean those six months, so the
 * snapshot lives with the other current-state sources and a period change leaves it
 * untouched — and `fetchAdminOccupancy()` is called with NO arguments, so the day it
 * answers for is the backend's own Zurich business date rather than the browser's.
 *
 * `current_receivables` is current state too, and it is NOT a separate request: it
 * rides inside the `revenue-analytics` response, org-wide and across all periods.
 * The window narrows that response's FLOW blocks and does not touch that one — the
 * aging service is called with no period at all, so there is no parameter that could
 * narrow it by mistake.
 *
 * Tenant cash deposits are current state too, but they are NOT read here: the page
 * already loads them and owns the details modal they open, so the tab takes them as a
 * prop. Fetching them a second time would duplicate a request and split ownership of
 * a working flow across two files.
 *
 * "GESAMTE ZEIT" IS REFUSED, NOT FAKED
 * ------------------------------------
 * Every period-aware contract here is bounded at 24 months, and an unbounded
 * expected revenue or occupancy denominator has no defined window. Falling back to
 * the to-month would be the collapse this slice removes; quietly answering with "the
 * last 24 months" would put a period on screen the label does not name. So the
 * window resolves to null, `unsupported` is true, and the period cards say so.
 *
 * Two reads still run in that mode, because both are honest without a window:
 * `revenue-analytics` with NO period parameters at all — it carries the current-state
 * receivables block, which is the same figure whichever month is asked for — and the
 * payment feed unwindowed, which for "Gesamte Zeit" is precisely the latest receipts
 * overall. The flow figures in that response are NOT rendered; the KPI grid's
 * unsupported branch is what guarantees it.
 *
 * PARTIAL FAILURE IS THE NORMAL CASE
 * ----------------------------------
 * Nine independent endpoints; one failing must not blank the page. Every source keeps
 * its own `{ data, error }`, so a dead cost service costs you the cost card and the
 * contribution margin, and nothing else — and the occupancy card keeps whichever of
 * its two halves answered.
 *
 * STALENESS
 * ---------
 * Responses are stamped with the WINDOW they were requested for and dropped if that
 * is no longer the selected one. `let cancelled` (the house pattern) only guards
 * unmount; it does not stop an April–September response landing after the user has
 * moved to September, because both effects belong to the same live component. The
 * token does — and it names the whole window, because "Apr–Sep" and "Sep alone"
 * would otherwise share a key.
 */

const EMPTY = { data: null, error: "" };

const initialState = {
  revenueAnalytics: EMPTY,
  companyInvoiced: EMPTY,
  costBreakdown: EMPTY,
  occupancyRoomDays: EMPTY,
  recentPayments: EMPTY,
  bexioStatus: EMPTY,
  alerts: EMPTY,
  criticalUnits: EMPTY,
  currentOccupancy: EMPTY,
};

/**
 * The run-up the trend chart shows for a SINGLE month.
 *
 * P3a made the backend's `trend_months` default to the window length, which is right
 * for a range — three months selected, three points — and would give a one-month
 * selection a one-point chart. The tab has always shown six months of run-up under a
 * single month, so that stays: the explicit value wins, and the default is used for
 * every range so the series is exactly the selection.
 */
const SINGLE_MONTH_TREND = 6;

export function useOverviewData(period, { enabled = true, paymentsLimit = 5 } = {}) {
  const [periodic, setPeriodic] = useState({
    revenueAnalytics: EMPTY,
    companyInvoiced: EMPTY,
    costBreakdown: EMPTY,
    occupancyRoomDays: EMPTY,
    recentPayments: EMPTY,
  });
  const [periodicLoading, setPeriodicLoading] = useState(true);
  const [current, setCurrent] = useState({
    bexioStatus: EMPTY,
    alerts: EMPTY,
    criticalUnits: EMPTY,
    currentOccupancy: EMPTY,
  });
  const [currentLoading, setCurrentLoading] = useState(true);
  const [periodicReloadToken, setPeriodicReloadToken] = useState(0);
  const [currentReloadToken, setCurrentReloadToken] = useState(0);

  const periodWindow = useMemo(() => selectOverviewWindow(period), [period]);
  // A primitive, so the effect below re-runs on a real period change and not on
  // every render that rebuilds an equal object. "" for "Gesamte Zeit", which is a
  // valid selection with no window rather than a missing one — `unsupported` is what
  // tells the two apart.
  const windowKey = overviewWindowKey(periodWindow);
  const unsupported = Boolean(period) && periodWindow === null;

  // The window the newest request belongs to. A ref, not state: it must be readable
  // by an in-flight promise that closed over an OLDER window, and comparing the
  // closure's own captured values would only ever compare them to themselves.
  const activeKeyRef = useRef("");

  // ── Period-aware sources ──
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    // "Gesamte Zeit" has no window, and its own key — so leaving it and coming back
    // still counts as a period change rather than resolving against a stale token.
    const requestedKey = windowKey || "all";
    activeKeyRef.current = requestedKey;

    setPeriodicLoading(true);

    const settle = (promise, fallback) =>
      promise.then(
        (data) => ({ data, error: "" }),
        (e) => ({ data: null, error: sanitizeClientErrorMessage(e?.message, fallback) })
      );
    const skip = () => Promise.resolve(EMPTY);

    // Without a window, only the two reads that are honest without one go out. The
    // analytics call carries NO period parameters: what is wanted from it is the
    // current-state receivables block, which answers the same whichever month the
    // server resolves for itself.
    const analyticsParams = periodWindow
      ? {
          ...requestParams(periodWindow),
          // A range gets the server's own default, which P3a made the window length,
          // so the series is exactly the selection.
          ...(periodWindow.isRange ? {} : { trendMonths: SINGLE_MONTH_TREND }),
        }
      : {};

    Promise.all([
      settle(
        fetchRevenueAnalytics(analyticsParams),
        "Umsatzdaten konnten nicht geladen werden."
      ),
      periodWindow
        ? settle(
            fetchBexioCompanyInvoiced(requestParams(periodWindow)),
            "Fakturierte Umsätze konnten nicht geladen werden."
          )
        : skip(),
      periodWindow
        ? settle(
            fetchDashboardCostBreakdown(requestParams(periodWindow)),
            "Betriebskosten konnten nicht geladen werden."
          )
        : skip(),
      periodWindow
        ? settle(
            fetchAdminOccupancyRoomDays(roomDaysParams(periodWindow)),
            "Auslastung konnte nicht geladen werden."
          )
        : skip(),
      settle(
        fetchBexioRecentPayments({
          limit: paymentsLimit,
          ...(periodWindow ? requestParams(periodWindow) : {}),
        }),
        "Zahlungseingänge konnten nicht geladen werden."
      ),
    ]).then(
      ([
        revenueAnalytics,
        companyInvoiced,
        costBreakdown,
        occupancyRoomDays,
        recentPayments,
      ]) => {
        if (cancelled) return;
        // A response for a period the user has already left is discarded, not
        // rendered. Effect cleanup alone is not enough: React runs cleanup for the
        // previous period, but a slow April–September request and a fast September
        // one can still resolve out of order, and whichever lands LAST would win.
        // The ref names the window that is actually selected right now.
        if (activeKeyRef.current !== requestedKey) return;
        setPeriodic({
          revenueAnalytics,
          companyInvoiced,
          costBreakdown,
          occupancyRoomDays,
          recentPayments,
        });
        setPeriodicLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
    // `periodWindow` is derived from `windowKey`; depending on the key keeps the effect
    // from re-firing on an equal-but-new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, windowKey, paymentsLimit, periodicReloadToken]);

  // ── Current-state sources: no period, so they do not re-run on a period change ──
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setCurrentLoading(true);

    const settle = (promise, fallback) =>
      promise.then(
        (data) => ({ data, error: "" }),
        (e) => ({ data: null, error: sanitizeClientErrorMessage(e?.message, fallback) })
      );

    Promise.all([
      settle(fetchBexioStatus(), "Bexio-Status konnte nicht geladen werden."),
      settle(fetchAdminAlerts(), "Warnungen konnten nicht geladen werden."),
      settle(
        fetchAdminCriticalUnits(),
        "Kritische Einheiten konnten nicht geladen werden."
      ),
      // ORG-WIDE, DELIBERATELY WITHOUT ARGUMENTS. `unit_id` would narrow the answer
      // to one unit, and `on_date` would have to be built from the device clock; the
      // endpoint's own default is `zurich_today()`, which is the business date this
      // company view is asking about.
      settle(
        fetchAdminOccupancy(),
        "Aktuelle Auslastung konnte nicht geladen werden."
      ),
    ]).then(([bexioStatus, alerts, criticalUnits, currentOccupancy]) => {
      if (cancelled) return;
      setCurrent({ bexioStatus, alerts, criticalUnits, currentOccupancy });
      setCurrentLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, currentReloadToken]);

  const reloadPeriodic = useCallback(() => setPeriodicReloadToken((n) => n + 1), []);
  const reloadCurrent = useCallback(() => setCurrentReloadToken((n) => n + 1), []);

  return {
    ...initialState,
    ...periodic,
    ...current,
    periodicLoading,
    currentLoading,
    window: periodWindow,
    /** The period names no answerable window ("Gesamte Zeit"). */
    unsupported,
    reloadPeriodic,
    reloadCurrent,
  };
}

/**
 * The four wire parameters the period-aware contracts share.
 *
 * `monthCount` and `isRange` are derived facts for the UI, not request parameters —
 * sending them would be inventing fields the endpoints do not accept.
 */
function requestParams(window) {
  return {
    year: window.year,
    month: window.month,
    to_year: window.to_year,
    to_month: window.to_month,
  };
}
